import type { ClaimRow, ContactRow, SiteDetail } from "@plenum/shared/api";
import { Link } from "@tanstack/react-router";
import { ApiError, useEnrich, useMe, usePatchContact } from "../../lib/api";
import { linkedInSearch } from "../../lib/map-styles";
import { Pill } from "../Badge";
import { type CiteSource, CiteSourceLine } from "../Cite";
import { useToast } from "../Toast";

/** Role groups in the order a sales call works through them. */
const ROLE_ORDER: Array<[string, string]> = [
  ["plant_manager", "Plant management"],
  ["operations", "Operations"],
  ["maintenance", "Maintenance"],
  ["engineering", "Engineering"],
  ["environmental", "Environmental and permits"],
  ["asset_management", "Asset management"],
  ["procurement", "Procurement"],
  ["executive", "Executive"],
  ["other", "Other"],
];

/**
 * The accepted claim that names this person: its quote and page are the contact's evidence. Matched
 * on the name, since contact rows carry the source but not the sentence (PRD §11.2).
 */
function evidenceFor(ct: ContactRow, claims: ClaimRow[]): ClaimRow | undefined {
  const name = ct.full_name.trim().toLowerCase();
  const named = claims.filter(
    (c) =>
      c.field === "contact" &&
      String((c.value as { full_name?: string } | null)?.full_name ?? "")
        .trim()
        .toLowerCase() === name,
  );
  return (
    named.find((c) => c.status === "accepted" && c.source.id === ct.primary_source?.id) ??
    named.find((c) => c.status === "accepted") ??
    named[0]
  );
}

export function ContactsTab({ detail }: { detail: SiteDetail }) {
  const patch = usePatchContact();
  const enrich = useEnrich();
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  const toast = useToast();
  const pendingContacts = detail.claims.filter(
    (c) => c.field === "contact" && c.status === "proposed",
  ).length;
  const act = async (contactId: number, p: Parameters<typeof patch.mutateAsync>[0]["patch"]) => {
    try {
      await patch.mutateAsync({ contactId, patch: p });
      toast(`Contact ${p.action}`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Failed", "danger");
    }
  };
  const paid = async () => {
    try {
      const r = await enrich.mutateAsync({
        site_ids: [detail.site.id],
        agents: ["contact_researcher"],
        allow_paid_apis: true,
      });
      toast(
        r.status === "pending_approval"
          ? `Approval #${r.approval_id} opened for paid contact enrichment`
          : "Contact enrichment started",
      );
    } catch (e) {
      toast(
        e instanceof ApiError ? (e.status === 403 ? "Admin role required" : e.message) : "Failed",
        "danger",
      );
    }
  };
  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(`${what} copied`);
    } catch {
      toast("Copy failed", "danger");
    }
  };

  const contacts = detail.contacts;
  const withEmail = contacts.filter((c) => c.email).length;
  const withPhone = contacts.filter((c) => c.phone).length;
  const groups = ROLE_ORDER.map(([role, label]) => ({
    role,
    label,
    rows: contacts.filter(
      (c) => (ROLE_ORDER.some(([r]) => r === c.role_category) ? c.role_category : "other") === role,
    ),
  })).filter((g) => g.rows.length > 0);
  let n = 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]"
          style={{ color: "var(--muted)" }}
          data-testid="contacts-summary"
        >
          <span>
            <span className="num" style={{ color: "var(--paper)" }}>
              {contacts.length}
            </span>{" "}
            {contacts.length === 1 ? "person" : "people"}
          </span>
          <span>
            <span className="num" style={{ color: "var(--paper)" }}>
              {withEmail}
            </span>{" "}
            with a printed email
          </span>
          <span>
            <span className="num" style={{ color: "var(--paper)" }}>
              {withPhone}
            </span>{" "}
            with a phone
          </span>
          {groups.length > 0 ? <span>{groups.map((g) => g.label).join(" · ")}</span> : null}
        </div>
        {isAdmin ? (
          <button
            type="button"
            className="btn btn-sm"
            onClick={paid}
            disabled={enrich.isPending}
            data-testid="enrich-paid"
          >
            Enrich with paid APIs (approval gate)
          </button>
        ) : null}
      </div>

      {pendingContacts > 0 ? (
        <p style={{ color: "var(--muted)" }} data-testid="contacts-pending">
          <span className="num">{pendingContacts}</span>{" "}
          {pendingContacts === 1 ? "person" : "people"} found in public records await review because
          a source disagreed or could not be re-fetched. Accept them in the{" "}
          <Link to="/review" search={{ site_id: detail.site.id }}>
            review queue
          </Link>{" "}
          and they appear here with their source.
        </p>
      ) : null}

      {contacts.length === 0 ? (
        pendingContacts === 0 ? (
          <p style={{ color: "var(--muted)" }}>
            No named person for this site in the public records searched (permits, regulatory
            dockets, company pages, trade press). The paid path above can look further with
            approval.
          </p>
        ) : null
      ) : (
        groups.map((g) => (
          <section key={g.role} className="contact-group">
            <h3 className="eyebrow mb-1">
              {g.label} <span className="num">{g.rows.length}</span>
            </h3>
            <ul
              className="rounded border"
              style={{ borderColor: "var(--line)", background: "var(--panel)" }}
            >
              {g.rows.map((ct) => {
                const ev = evidenceFor(ct, detail.claims);
                n += 1;
                const src: CiteSource | null = ct.primary_source
                  ? {
                      n,
                      url: ct.primary_source.url,
                      title: ct.primary_source.title,
                      sourceType: ct.primary_source.source_type,
                      quote: ev?.quote ?? null,
                      retrievedAt: ev?.source.retrieved_at ?? null,
                    }
                  : null;
                return (
                  <li key={ct.id} className="contact-row">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-medium">{ct.full_name}</span>
                        <span style={{ color: "var(--muted)" }}>
                          {ct.title ?? ct.role_category}
                        </span>
                        {ct.review_status !== "accepted" ? (
                          <Pill tone="muted">{ct.review_status}</Pill>
                        ) : null}
                        {ct.private ? <Pill tone="gold">private to org</Pill> : null}
                        {ct.paid_api_used ? <Pill tone="gold">paid API</Pill> : null}
                        {ct.do_not_contact ? <Pill tone="danger">DNC</Pill> : null}
                      </div>
                      {ct.email || ct.phone ? (
                        <div className="mt-0.5 flex flex-wrap gap-x-4">
                          {ct.email ? (
                            <span className="channel">
                              <a href={`mailto:${ct.email}`}>{ct.email}</a>
                              {ct.email_status !== "public_record" ? (
                                <Pill
                                  tone={ct.email_status === "verified_deliverable" ? "ok" : "muted"}
                                >
                                  {ct.email_status.replace(/_/g, " ")}
                                </Pill>
                              ) : null}
                              <button
                                type="button"
                                className="copy-btn"
                                onClick={() => copy(ct.email ?? "", "Email")}
                                aria-label={`Copy email for ${ct.full_name}`}
                              >
                                copy
                              </button>
                            </span>
                          ) : null}
                          {ct.phone ? (
                            <span className="channel">
                              <a href={`tel:${ct.phone.replace(/[^\d+]/g, "")}`}>{ct.phone}</a>
                              {ct.phone_type && ct.phone_type !== "unknown" ? (
                                <span style={{ color: "var(--muted-2)" }}>
                                  {ct.phone_type.replace(/_/g, " ")}
                                </span>
                              ) : null}
                              <button
                                type="button"
                                className="copy-btn"
                                onClick={() => copy(ct.phone ?? "", "Phone")}
                                aria-label={`Copy phone for ${ct.full_name}`}
                              >
                                copy
                              </button>
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="mt-0.5 min-w-0">
                        {src ? (
                          <CiteSourceLine
                            source={src}
                            trailing={<LinkedIn ct={ct} detail={detail} />}
                          />
                        ) : (
                          <LinkedIn ct={ct} detail={detail} />
                        )}
                      </div>
                    </div>
                    <div className="contact-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => act(ct.id, { action: "verify" })}
                      >
                        Verify
                      </button>
                      {ct.review_status !== "accepted" ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => act(ct.id, { action: "accept" })}
                        >
                          Accept
                        </button>
                      ) : null}
                      {!ct.do_not_contact ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          style={{ color: "var(--danger)" }}
                          onClick={() =>
                            act(ct.id, { action: "dnc", dnc_reason: "requested by contact" })
                          }
                        >
                          Mark DNC
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
      <p className="text-[11px]" style={{ color: "var(--muted-2)" }}>
        Every person comes from a public page Plenum captured; "quote" shows the sentence that names
        them. Emails and phones appear only where that page prints them beside the name. The
        LinkedIn link is built in the browser; nothing is fetched from LinkedIn.
      </p>
    </div>
  );
}

function LinkedIn({ ct, detail }: { ct: ContactRow; detail: SiteDetail }) {
  return (
    <a
      className="text-[11px]"
      style={{ marginLeft: 6 }}
      href={linkedInSearch(ct.full_name, detail.site.operator?.name ?? null)}
      target="_blank"
      rel="noreferrer"
    >
      LinkedIn search ↗
    </a>
  );
}
